import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lightbulb, LogOut, Shield, Bell, User as UserIcon, LayoutDashboard, Folder, PlusSquare, HelpCircle, Menu, X, BarChart3, MessageCircle, Phone, Mail, Clock, CheckCircle2, Users, FileSpreadsheet, ChevronDown, ChevronRight, TrendingUp, Network, Workflow, FileCheck, ShieldAlert, Target, LayoutGrid } from 'lucide-react';
import { apiClient } from '../api/client.ts';

interface LayoutProps {
  children: React.ReactNode;
  title: string;
}

export function Layout({ children, title }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const userName = localStorage.getItem('userName') || 'Employee';
  const userEmail = localStorage.getItem('userEmail') || '';
  const userRole = localStorage.getItem('userRole') || 'EMPLOYEE';
  const userSubCoreRole = localStorage.getItem('userSubCoreRole') || '';

  const displayRole = (userRole === 'COMMITTEE_MEMBER' && userSubCoreRole === 'EXECUTOR')
    ? 'COMMITTEE_EXECUTOR'
    : userRole;

  const [showProfile, setShowProfile] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(window.innerWidth < 1280);
  const [showSupportWidget, setShowSupportWidget] = useState(false);
  const [expandedNav, setExpandedNav] = useState('');

  // Auto-collapse sidebar on medium/large screens, expand on extra large screens
  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024 && window.innerWidth < 1280) {
        setIsSidebarCollapsed(true);
      } else if (window.innerWidth >= 1280) {
        setIsSidebarCollapsed(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch pending ideas for Department Head (COMMITTEE_HEAD)
  const { data: globalStats } = useQuery({
    queryKey: ['adminStats'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/stats');
      return res.data.data;
    },
    enabled: userRole === 'COMMITTEE_HEAD',
    refetchInterval: 30000 // refresh every 30s
  });

  const pendingApprovalsCount = globalStats?.kpis?.pendingHeadApproval || 0;

  const { data: inAppNotifications } = useQuery({
    queryKey: ['myNotifications'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/notifications');
      return (res.data.data || []) as { id: string; ideaId: string | null; message: string; createdAt: string }[];
    },
    enabled: userRole === 'COMMITTEE_HEAD',
    refetchInterval: 15000,
  });

  const notificationList = inAppNotifications || [];
  const bellCount = pendingApprovalsCount + notificationList.length;

  const { data: pendingIdeasRes, isLoading: pendingIdeasLoading } = useQuery({
    queryKey: ['adminPendingIdeas'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/ideas', { params: { status: 'PENDING_HEAD_APPROVAL', limit: 5 } });
      return res.data.data;
    },
    enabled: userRole === 'COMMITTEE_HEAD' && showNotifications,
  });

  const pendingIdeasList = pendingIdeasRes?.items || [];

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (err) {
      console.error('Logout error', err);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userRole');
      navigate('/login');
    }
  };

  let dashboardPath = '/dashboard';
  let dashboardName = 'Dashboard';

  if (userRole === 'ADMIN' || userRole === 'APPROVAL_TEAM') {
    dashboardPath = '/admin';
    dashboardName = 'Admin Portal';
  } else if (userRole === 'COMMITTEE_HEAD') {
    dashboardPath = '/committee-head';
    dashboardName = 'Head Portal';
  } else if (userRole === 'COMMITTEE_MEMBER') {
    dashboardPath = '/committee-member';
    dashboardName = userSubCoreRole === 'EXECUTOR' ? 'Executor Portal' : 'Member Portal';
  } else if (userRole === 'LEADER') {
    dashboardPath = '/leader';
    dashboardName = 'Leader Portal';
  } else if (userRole === 'EXTERNAL_COMMITTEE') {
    dashboardPath = '/external-committee';
    dashboardName = 'External Portal';
  } else if (userRole === 'EXECUTION_COMMITTEE') {
    dashboardPath = '/execution-committee';
    dashboardName = 'Execution Portal';
  } else if (userRole === 'MIS_COMMITTEE') {
    dashboardPath = '/mis';
    dashboardName = 'MIS Portal';
  }

  const isExecutor = userSubCoreRole === 'EXECUTOR' || userRole === 'EXECUTION_COMMITTEE' || userRole === 'EXTERNAL_COMMITTEE';
  const isModerator = userRole === 'LEADER' || userRole === 'COMMITTEE_HEAD' || userRole === 'COMMITTEE_MEMBER';
  const isExternalOrExecution = userRole === 'EXTERNAL_COMMITTEE' || userRole === 'EXECUTION_COMMITTEE';

  const navItems = userRole === 'MIS_COMMITTEE' ? [
    { name: 'MIS Portal', path: '/mis', icon: Shield },
    { name: 'My Ideas', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Submit Idea', path: '/ideas/new', icon: PlusSquare },
    { name: 'Help & Support', path: '/help', icon: HelpCircle },
  ] : userRole === 'ADMIN' ? [
    { name: dashboardName, path: dashboardPath, icon: Shield },
    { name: 'User Master', path: '/admin/users', icon: UserIcon },
    // { name: 'Department Mappings (Old)', path: '/admin/department-mapping', icon: Folder },
    { name: 'Department Groupings', path: '/admin/department-grouping', icon: Network },
    { name: 'Committee Mappings', path: '/admin/department-mapping-v2', icon: Workflow },
    {
      name: 'Bulk Imports', path: '#', icon: FileSpreadsheet, subItems: [
        { name: 'Import Bulk Assignee', path: '/admin/import-bulk-assignee', icon: FileSpreadsheet },
        { name: 'Bulk Evaluation Import', path: '/admin/bulk-evaluate-import', icon: FileCheck },
        { name: 'Bulk Status Import', path: '/admin/bulk-status-import', icon: ShieldAlert },
      ]
    },
    { name: 'Idea Audit & Integrity', path: '/admin/audit', icon: ShieldAlert },
    {
      name: 'Reports DOM 680', path: '#', icon: BarChart3, subItems: [
        { name: 'Dashboard', path: '/admin/reports/dom680', icon: BarChart3 },
        { name: 'Targets Settings', path: '/admin/reports/dom680/targets', icon: Target },
        { name: 'Incentive Dashboard', path: '/admin/reports/metrix', icon: LayoutGrid },
        { name: 'Reports & KPIs', path: '/admin/reports', icon: BarChart3 },
        { name: 'Advanced Reports', path: '/admin/reports/advanced', icon: TrendingUp },
      ]
    },
    { name: 'Help & Support', path: '/help', icon: HelpCircle },
  ] : userRole === 'APPROVAL_TEAM' ? [
    { name: dashboardName, path: dashboardPath, icon: Shield },
    { name: 'Help & Support', path: '/help', icon: HelpCircle },
  ] : (isModerator || isExternalOrExecution) ? [
    { name: dashboardName, path: dashboardPath, icon: Shield },
    { name: 'My Ideas', path: '/dashboard', icon: Folder },
    ...(userRole === 'COMMITTEE_HEAD' ? [
      {
        name: 'Bulk Imports', path: '#', icon: FileSpreadsheet, subItems: [
          { name: 'Import Bulk Assignee', path: '/admin/import-bulk-assignee', icon: FileSpreadsheet },
          { name: 'Bulk Evaluation Import', path: '/admin/bulk-evaluate-import', icon: FileCheck },
          { name: 'Bulk Status Import', path: '/admin/bulk-status-import', icon: ShieldAlert },
        ]
      }
    ] : (userRole === 'COMMITTEE_MEMBER' && !isExecutor) ? [
      {
        name: 'Bulk Imports', path: '#', icon: FileSpreadsheet, subItems: [
          { name: 'Bulk Evaluation Import', path: '/admin/bulk-evaluate-import', icon: FileCheck },
          { name: 'Bulk Status Import', path: '/admin/bulk-status-import', icon: ShieldAlert },
        ]
      }
    ] : []),
    {
      name: 'Reports DOM 680', path: '#', icon: BarChart3, subItems: [
        { name: 'Dashboard', path: '/admin/reports/dom680', icon: BarChart3 },
      ]
    },
    { name: 'Submit Idea', path: '/ideas/new', icon: PlusSquare },
    { name: 'Help & Support', path: '/help', icon: HelpCircle },
  ] : [
    { name: 'My Ideas', path: '/dashboard', icon: LayoutDashboard },
    { name: 'Submit Idea', path: '/ideas/new', icon: PlusSquare },
    { name: 'Help & Support', path: '/help', icon: HelpCircle },
  ];

  const isAuthenticated = !!localStorage.getItem('accessToken');

  const renderSupportWidget = () => (
    <div className="fixed bottom-6 right-6 z-[100]">
      {showSupportWidget && (
        <div className="absolute bottom-16 right-0 w-80 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden mb-4 animate-in slide-in-from-bottom-5">
          <div className="bg-[#1C4ED8] p-4 flex justify-between items-center text-white">
            <h3 className="font-semibold text-lg">Help & Support</h3>
            <button onClick={() => setShowSupportWidget(false)} className="text-white hover:text-slate-200">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
            <p className="text-sm text-slate-600">Need assistance? We're here to help.</p>
            <div className="flex items-start space-x-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
              <Phone className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-slate-800">Phone</p>
                <a href="tel:+919043995078" className="text-xs text-blue-600 hover:underline">+91 90439 95078</a>
              </div>
            </div>
            <div className="flex items-start space-x-3 bg-slate-50 p-3 rounded-lg border border-slate-100">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-slate-800">Email</p>
                <a href="mailto:sasitharan.nr@casagrand.co.in" className="text-xs text-blue-600 hover:underline break-all">sasitharan.nr@casagrand.co.in</a>
              </div>
            </div>
            <div className="bg-blue-50 text-blue-800 p-3 rounded-lg flex items-start space-x-2">
              <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs font-medium">Support Hours: Mon-Sat, 9:30 AM – 6:30 PM</p>
            </div>
            <Link to="/help" onClick={() => setShowSupportWidget(false)} className="block w-full text-center py-2 text-sm font-semibold text-[#1C4ED8] bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
              View Full Support Guide
            </Link>
          </div>
        </div>
      )}
      <button
        onClick={() => setShowSupportWidget(!showSupportWidget)}
        className="w-14 h-14 bg-[#1C4ED8] hover:bg-blue-700 text-white rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105 active:scale-95"
      >
        <MessageCircle className="w-7 h-7" />
      </button>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F4F7FA] font-sans flex flex-col overflow-y-auto">
        <header className="bg-white border-b border-slate-200 h-20 px-6 flex items-center justify-between shrink-0">
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            <div className="bg-[#1C4ED8] p-1.5 rounded-lg text-white">
              <Lightbulb className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-bold font-display text-slate-800">DOM680</h1>
          </Link>
          {/* <Link
            to="/login"
            className="bg-[#1C4ED8] hover:bg-blue-700 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-all shadow-sm"
          >
            Sign In
          </Link> */}
        </header>
        <main className="flex-1 p-6 md:p-8 max-w-4xl w-full mx-auto">
          {children}
        </main>
        {renderSupportWidget()}
      </div>
    );
  }

  const isEffectivelyCollapsed = isSidebarCollapsed && !mobileMenuOpen;

  return (
    <div className="min-h-screen bg-[#F4F7FA] font-sans flex overflow-hidden">
      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Left Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 ${isEffectivelyCollapsed ? 'w-20' : 'w-64'} bg-[#0B1A30] text-slate-300 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:flex lg:flex-col ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className={`flex items-center ${isEffectivelyCollapsed ? 'justify-center' : 'justify-between'} p-6 h-20 shrink-0`}>
          <div className="flex items-center space-x-3">
            <div className="bg-[#1C4ED8] p-1.5 rounded-lg text-white shrink-0">
              <Lightbulb className="w-5 h-5" />
            </div>
            {!isEffectivelyCollapsed && (
              <div className="overflow-hidden">
                <Link to={userRole === 'MIS_COMMITTEE' ? '/mis' : '/dashboard'} className="text-lg font-bold text-white tracking-wide font-display whitespace-nowrap">
                  Idea Portal
                </Link>
              </div>
            )}
          </div>
          <button className="lg:hidden text-slate-400" onClick={() => setMobileMenuOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navItems.map((item: any) => {
            const Icon = item.icon;
            const hasSubItems = item.subItems && item.subItems.length > 0;
            const isSubActive = hasSubItems && item.subItems.some((sub: any) => location.pathname === sub.path);
            const isActive = !hasSubItems && (location.pathname === item.path || (item.path === '/mis' && location.pathname.startsWith('/mis')) || ((item.path === '/ideas' || item.path === '/dashboard') && (location.pathname.startsWith('/ideas/edit') || location.pathname === '/ideas')));

            if (hasSubItems) {
              const isOpen = isSubActive || expandedNav === item.name;
              return (
                <div key={item.name}>
                  <button
                    onClick={() => {
                      if (isEffectivelyCollapsed) setIsSidebarCollapsed(false);
                      setExpandedNav(isOpen && !isSubActive ? '' : item.name);
                    }}
                    title={isEffectivelyCollapsed ? item.name : undefined}
                    className={`flex items-center justify-between w-full ${isEffectivelyCollapsed ? 'px-0 justify-center' : 'px-3'} py-2.5 rounded-lg transition-colors group ${isSubActive ? 'bg-slate-800 text-white' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-5 h-5 shrink-0 ${isSubActive ? 'text-[#1C4ED8]' : 'text-slate-500 group-hover:text-slate-300'} ${isEffectivelyCollapsed ? 'mx-auto' : ''}`} />
                      {!isEffectivelyCollapsed && <span className="text-sm whitespace-nowrap">{item.name}</span>}
                    </div>
                    {!isEffectivelyCollapsed && (
                      isOpen ? <ChevronDown className="w-4 h-4 text-slate-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-500 shrink-0" />
                    )}
                  </button>
                  {isOpen && !isEffectivelyCollapsed && (
                    <div className="ml-4 mt-1 space-y-0.5 border-l border-slate-700 pl-3">
                      {item.subItems.map((sub: any) => {
                        const SubIcon = sub.icon;
                        const isSubItemActive = location.pathname === sub.path;
                        return (
                          <Link
                            key={sub.name}
                            to={sub.path}
                            className={`flex items-center space-x-2 px-2.5 py-2 rounded-lg transition-colors text-xs font-semibold ${isSubItemActive
                              ? 'bg-[#1C4ED8] text-white font-bold shadow-md'
                              : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                              }`}
                          >
                            <SubIcon className={`w-4 h-4 shrink-0 ${isSubItemActive ? 'text-white' : 'text-slate-500'}`} />
                            <span className="truncate">{sub.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.name}
                to={item.path}
                title={isEffectivelyCollapsed ? item.name : undefined}
                className={`flex items-center justify-between ${isEffectivelyCollapsed ? 'px-0 justify-center' : 'px-3'} py-2.5 rounded-lg transition-colors group ${isActive
                  ? 'bg-[#1C4ED8] text-white font-medium shadow-md'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                  }`}
              >
                <div className="flex items-center space-x-3">
                  <Icon className={`w-5 h-5 shrink-0 ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-slate-300'} ${isEffectivelyCollapsed ? 'mx-auto' : ''}`} />
                  {!isEffectivelyCollapsed && <span className="text-sm whitespace-nowrap">{item.name}</span>}
                </div>
                {!isEffectivelyCollapsed && ('badge' in item) && (
                  <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
                    {(item as any).badge}
                  </span>
                )}
                {isEffectivelyCollapsed && ('badge' in item) && (
                  <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full"></div>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            title={isEffectivelyCollapsed ? "Logout" : undefined}
            className={`flex items-center ${isEffectivelyCollapsed ? 'justify-center px-0' : 'space-x-3 px-3'} w-full py-2.5 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-red-400 transition-colors`}
          >
            <LogOut className="w-5 h-5 shrink-0" />
            {!isEffectivelyCollapsed && <span className="text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 h-20 px-6 flex items-center justify-between shrink-0">
          <div className="flex items-center">
            <button className="lg:hidden mr-4 text-slate-500 hover:bg-slate-100 p-2 rounded-lg transition-colors" onClick={() => setMobileMenuOpen(true)}>
              <Menu className="w-5 h-5" />
            </button>
            <button className="hidden lg:block mr-4 text-slate-500 hover:bg-slate-100 p-2 rounded-lg transition-colors" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold font-display text-slate-800 hidden sm:block">{title}</h1>
          </div>

          <div className="flex items-center space-x-6">
            {(userRole === 'LEADER' || userRole === 'COMMITTEE_HEAD' || userRole === 'COMMITTEE_MEMBER') && (
              <Link
                to="/my-team"
                className="text-slate-400 hover:text-slate-600 transition-colors"
                title="My Team"
              >
                <Users className="w-5 h-5" />
              </Link>
            )}

            {userRole === 'COMMITTEE_HEAD' && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <Bell className="w-5 h-5" />
                  {bellCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white min-w-4 h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center border-2 border-white">
                      {bellCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-3 w-96 rounded-xl bg-white border border-slate-200 shadow-xl overflow-hidden py-1 z-50">
                    <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                      <p className="text-sm font-bold text-slate-800">Pending Final Approvals</p>
                      <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{pendingApprovalsCount}</span>
                    </div>

                    <div className="max-h-[300px] overflow-y-auto">
                      {pendingIdeasLoading ? (
                        <div className="px-4 py-4 text-center">
                          <p className="text-xs text-slate-500 animate-pulse">Loading...</p>
                        </div>
                      ) : pendingIdeasList.length > 0 ? (
                        pendingIdeasList.map((idea: any) => (
                          <Link
                            key={idea.id}
                            to={`/ideas/${idea.id}`}
                            onClick={() => setShowNotifications(false)}
                            className="block px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                          >
                            <p className="text-sm font-bold text-slate-800 truncate" title={idea.title}>{idea.title}</p>
                            <p className="text-xs text-slate-500 mt-1 truncate">ID: CG{idea.id.substring(0, 7)} • {idea.employee?.name}</p>
                            <p className="text-[10px] text-indigo-600 font-semibold mt-1">Click to review and approve</p>
                          </Link>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-center">
                          <p className="text-sm text-slate-500">No pending approvals</p>
                        </div>
                      )}
                    </div>

                    {pendingApprovalsCount > 5 && (
                      <Link
                        to="/admin"
                        onClick={() => setShowNotifications(false)}
                        className="block px-4 py-2.5 text-center text-xs font-bold text-indigo-600 bg-slate-50 hover:bg-slate-100 transition-colors border-t border-slate-100"
                      >
                        View All Pending Approvals
                      </Link>
                    )}

                    {notificationList.length > 0 && (
                      <>
                        <div className="px-4 py-3 border-y border-slate-100 bg-slate-50 flex items-center justify-between">
                          <p className="text-sm font-bold text-slate-800">Notifications</p>
                          <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{notificationList.length}</span>
                        </div>
                        <div className="max-h-[300px] overflow-y-auto">
                          {notificationList.map((note) => (
                            <Link
                              key={note.id}
                              to={note.ideaId ? `/ideas/${note.ideaId}` : '/committee-head'}
                              onClick={() => {
                                setShowNotifications(false);
                                apiClient.patch(`/admin/notifications/${note.id}/read`).finally(() => {
                                  queryClient.invalidateQueries({ queryKey: ['myNotifications'] });
                                });
                              }}
                              className="block px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                            >
                              <p className="text-xs text-slate-700 leading-relaxed">{note.message}</p>
                            </Link>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="relative">
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="flex items-center space-x-3 focus:outline-none"
              >
                <div className="text-right hidden sm:block">
                  <p className="text-sm font-bold text-slate-700 leading-tight">{userName}</p>
                  <p className="text-xs text-slate-500">{displayRole}</p>
                </div>
                <div className="w-9 h-9 rounded-full bg-blue-100 border border-blue-200 flex items-center justify-center font-display text-blue-700 font-bold text-sm">
                  {userName.charAt(0)}
                </div>
              </button>

              {showProfile && (
                <div className="absolute right-0 mt-3 w-56 rounded-xl bg-white border border-slate-200 shadow-xl overflow-hidden py-1 z-50">
                  <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
                    <p className="text-xs text-slate-500">Signed in as</p>
                    <p className="text-sm font-semibold truncate text-slate-800">{userEmail}</p>
                  </div>
                  <Link
                    to="/profile"
                    className="flex items-center space-x-2 px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors border-b border-slate-100"
                  >
                    <UserIcon className="w-4 h-4" />
                    <span>My Profile</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-2 px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 hover:text-red-600 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main Workspace */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="max-w-7xl mx-auto">
            {/* Mobile Title */}
            <h1 className="text-2xl font-bold font-display text-slate-800 mb-6 sm:hidden">{title}</h1>
            {children}
          </div>
        </main>
        {renderSupportWidget()}
      </div>
    </div>
  );
}
